// components/schedule/StaffUtilizationDialog.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Box,
  Typography, CircularProgress
} from '@mui/material';
import { format, getDaysInMonth } from 'date-fns';
import { Schedule } from '../../types/schedule';
import { scheduleService } from '../../services/schedule';

interface StaffUtilizationDialogProps {
  open: boolean;
  onClose: () => void;
  month: Date;
  schedules: Schedule[];
}

interface StaffUtilization {
  id: number;
  name: string;
  team: string;
  workDays: number;
  utilizationRate: number;
  mainProjects: { name: string; count: number }[];
}

const StaffUtilizationDialog: React.FC<StaffUtilizationDialogProps> = ({
  open, onClose, month, schedules
}) => {
  const [loading, setLoading] = useState(false);
  const [staffData, setStaffData] = useState<StaffUtilization[]>([]);
  
  useEffect(() => {
    if (open) {
      calculateUtilization();
    }
  }, [open, schedules]);
  
  const calculateUtilization = () => {
    setLoading(true);
    
    try {
      // 作業員マスタ情報を取得（実際にはscheduleServiceから取得）
      const staffMembers = [
        { id: 1, name: '田中', team: '設備' },
        { id: 2, name: '佐藤', team: '設備' },
        { id: 3, name: '鈴木', team: '仮設' },
        { id: 4, name: '加藤', team: '仮設' },
        { id: 5, name: '山本', team: '設備' }
      ];
      
      // 営業日数（仮定）
      const businessDays = 22;
      
      // 各作業員の稼働データを計算
      const utilization = staffMembers.map(staff => {
        // この作業員が含まれるスケジュールをカウント
        const staffSchedules = schedules.filter(s => 
          s.memberData?.some(m => 
            (m.Member_ID === staff.id) || 
            (m.displayName && m.displayName.includes(staff.name))
          )
        );
        
        // 作業日数をカウント（同日に複数の現場があっても1日とカウント）
        const workDates = new Set(staffSchedules.map(s => s.scheduleDate));
        const workDays = workDates.size;
        
        // 稼働率計算
        const utilizationRate = (workDays / businessDays) * 100;
        
        // 主要プロジェクト集計
        const projectCounts: { [key: string]: number } = {};
        staffSchedules.forEach(s => {
          const siteName = s.siteName || `現場 ${s.constructionId}`;
          projectCounts[siteName] = (projectCounts[siteName] || 0) + 1;
        });
        
        // 降順ソートして上位3件取得
        const mainProjects = Object.entries(projectCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);
        
        return {
          id: staff.id,
          name: staff.name,
          team: staff.team,
          workDays,
          utilizationRate,
          mainProjects
        };
      });
      
      setStaffData(utilization);
    } catch (error) {
      console.error('Error calculating utilization:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // 稼働率に基づく色を取得
  const getUtilizationColor = (rate: number) => {
    if (rate >= 90) return '#f44336'; // 赤 - 非常に高負荷
    if (rate >= 60) return '#4caf50'; // 緑 - 適正負荷
    return '#ffeb3b';                 // 黄 - 低負荷
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {format(month, 'yyyy年MM月')}の作業員稼働状況
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>作業員</TableCell>
                  <TableCell>稼働日数</TableCell>
                  <TableCell>稼働率</TableCell>
                  <TableCell>主な担当現場</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staffData.map(staff => (
                  <TableRow key={staff.id}>
                    <TableCell>
                      {staff.name}({staff.team})
                    </TableCell>
                    <TableCell>{staff.workDays}日</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: getUtilizationColor(staff.utilizationRate),
                            mr: 1
                          }}
                        />
                        {staff.utilizationRate.toFixed(0)}%
                      </Box>
                    </TableCell>
                    <TableCell>
                      {staff.mainProjects.map((project, i) => (
                        <Typography key={i} variant="body2">
                          {project.name} ({project.count}日)
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <Box mt={2} p={1} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="subtitle2">凡例:</Typography>
              <Box display="flex" mt={1}>
                <Box display="flex" alignItems="center" mr={3}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#f44336', mr: 1 }} />
                  <Typography variant="body2">非常に高負荷 (90%以上)</Typography>
                </Box>
                <Box display="flex" alignItems="center" mr={3}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50', mr: 1 }} />
                  <Typography variant="body2">適正負荷 (60%-90%)</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#ffeb3b', mr: 1 }} />
                  <Typography variant="body2">低負荷 (60%未満)</Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffUtilizationDialog;